import axios, { AxiosError } from "axios";
import * as T from '../utilities/frontendTypes';
import Cookies from "universal-cookie";
import { getAuthHeader, getRouteNameFromUserRole } from "../utilities/helperFunctions";
import instance, { resetInterceptorState, getIsLoggingOut, setLoggingOut } from '../middleware/HTTPInterceptors';
import ErrorResponse from '../utilities/ErrorResponse';
import { Address } from '../Components/Form/AddressFields';
import { apiGet, apiPut } from './apiClient';

interface AxiosErrorData {
  message?: string;
  error?: string;
  requiresVerification?: boolean;
  accountLocked?: boolean;
}

// Clean axios instance for token refresh — bypasses interceptors to prevent recursive interception
const refreshAxios = axios.create({
  baseURL: import.meta.env.VITE_ENV_URL,
  withCredentials: true
});


export async function getUserInfo(): Promise<T.UserInfoResponse> {
  return apiGet<T.UserInfoResponse>("/api/users/user-info");
}

/**
 * Effective SoW product price caps for a GIVEN region. Reviewers (admins, IP
 * Advisors) review SoWs from regions other than their own, so they fetch caps by
 * the SoW's region rather than the provider-only /api/providers/sow-caps endpoint.
 */
export async function getSowCapsForRegion(region: string): Promise<T.SowCapsResponse> {
  return apiGet<T.SowCapsResponse>(`/api/users/sow-caps/${region}`);
}

export async function getUserProfile(userRole: T.UserRole): Promise<T.UserProfileResponse> {
  const routeName = getRouteNameFromUserRole(userRole);
  return apiGet<T.UserProfileResponse>(`/api/${routeName}/profile`);
}

export async function updateUserProfile(userRole: T.UserRole, updatedProfile: T.AdminProfile | T.StartupFounderProfile | T.ServiceProviderProfile | T.TempAdminProfile | T.ExpertProfile | T.ServiceProviderProfile | T.TempGrowthCoachProfile ): Promise<T.UserProfileResponse> {
  const routeName = getRouteNameFromUserRole(userRole);
  return apiPut<T.UserProfileResponse>(`/api/${routeName}/profile`, updatedProfile);
}

export type RegisterInfo = {
  username: string,
  email: string,
  password: string,
  userRole: T.UserRole,
  userRegion: T.UserRegion,
  sLASigningTimestamp: Date,
  acceptedTermsOfService: boolean,
  address: Address,
  settings: {
    lang: T.LangState
  }
}
export type RegisterResponse = {
  success: boolean;
  requiresVerification?: boolean;
  message?: string;
  token?: string;
}

export async function registerUser(reqBody: RegisterInfo): Promise<RegisterResponse> {
  try {
    const res = await instance.post("/api/auth/register", reqBody);

    // If registration requires email verification, don't set cookie
    if (res.data.requiresVerification) {
      return {
        success: res.data.success,
        requiresVerification: true,
        message: res.data.message
      };
    }

    // Legacy flow: set cookie if token is returned
    if (res.data.success && res.data.token) {
      const cookie = new Cookies();
      cookie.set("authToken", res.data.token, { path: '/' });
    }

    return {
      success: res.data.success,
      message: res.data.message
    };

  } catch (err: unknown) {
    const axErr = err instanceof AxiosError ? err : null;
    const data = axErr?.response?.data as AxiosErrorData | undefined;
    throw new ErrorResponse(data?.message || "Registration failed", axErr?.response?.status || 500);
  }
}

export type LoginResponse = {
  success: boolean;
  requiresVerification?: boolean;
  accountLocked?: boolean;
  message?: string;
  error?: string;
}

export async function loginUser(reqBody: T.LoginInfo): Promise<LoginResponse> {
  try {
    const res = await instance.post("/api/auth/login", reqBody);

    const cookie = new Cookies();
    cookie.set("authToken", res.data.token, { path: '/' });
    return { success: res.data.success };

  } catch (err: unknown) {
    const response = (err instanceof AxiosError ? err.response?.data : null) as AxiosErrorData | null ?? {};

    // Handle email verification required
    if (response.requiresVerification) {
      return {
        success: false,
        requiresVerification: true,
        message: response.message
      };
    }

    // Handle account locked
    if (response.accountLocked) {
      return {
        success: false,
        accountLocked: true,
        message: response.message
      };
    }

    // Generic error
    return {
      success: false,
      error: response.message || response.error || "Login failed"
    };
  }
}

export async function logoutUser() {
  const cookie = new Cookies();

  // Set logout flag BEFORE any async work to prevent race conditions
  // with concurrent token refresh attempts
  setLoggingOut(true);

  try {
    // Logout endpoint only needs refreshToken cookie (httpOnly), not access token header
    // Try to include access token if available, but don't fail if it's missing/expired
    const token = cookie.get("authToken");
    const config = token
      ? { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      : {};

    await instance.delete("/api/auth/logout", config);

    // Small delay to ensure the browser processes the Set-Cookie header that clears
    // the httpOnly refreshToken cookie before we redirect
    await new Promise(resolve => setTimeout(resolve, 100));
  } catch (err: unknown) {
    const axErr = err instanceof AxiosError ? err : null;
    const errMsg = (axErr?.response?.data as AxiosErrorData | undefined)?.error || axErr?.message || "Unknown error";
    console.error("Backend logout failed:", errMsg);
  } finally {
    // Always clean up client-side state, even if backend call failed
    cookie.remove("authToken", { path: '/' });
    resetInterceptorState(); // This also resets isLoggingOut flag
  }
}

export async function checkIsUserAlive(): Promise<boolean> {

  try {
    const cookie = new Cookies();
    let accessToken = await cookie.get('authToken');

    // If no access token, attempt refresh (refreshToken is httpOnly so we can't
    // read it from JS, but it's sent automatically with the request)
    // BUT: don't auto-refresh if we're on the login page or during logout
    if (!accessToken) {
      // Skip auto-refresh if user is explicitly on login page (they're trying to log in/out)
      const isOnLoginPage = window.location.pathname === '/login';
      if (isOnLoginPage) {
        return false;
      }

      accessToken = await refreshAccessToken();
      if (!accessToken) return false;
    }

    // not using the getAuthHeader helper function here to avoid having to get token twice
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      }
    }
    const res = await instance.get("/api/private", config);

    return res.data.success;

  } catch (_err: unknown) {
    return false;
  }
}

export async function getRegionalMembershipId(): Promise<{success: boolean, membershipId: T.MembershipId}> {

  try {

    const config = {
      headers: await getAuthHeader(),
    }

    const res = await instance.get("/api/users/regional-membership-id", config);

    if (res.status === 200) {
      return res.data
    }

    throw new ErrorResponse("Request unsuccessful", 400);

  } catch (err: unknown) {
    const axErr = err instanceof AxiosError ? err : null;
    const data = axErr?.response?.data as AxiosErrorData | undefined;
    throw new ErrorResponse(data?.error || "Failed to get regional membership ID", axErr?.response?.status || 500);
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  // Guard against token refresh after logout has been initiated
  // This prevents race conditions where logout fires while refresh is in progress
  if (getIsLoggingOut()) {
    return null;
  }

  try {
    const res = await refreshAxios.post("/api/auth/refresh");
    const cookie = new Cookies();
    const newAccessToken = res.data.token;
    cookie.set("authToken", newAccessToken, { path: '/' });
    return newAccessToken;
  } catch (err: unknown) {
    const axErr = err instanceof AxiosError ? err : null;
    const errMsg = (axErr?.response?.data as AxiosErrorData | undefined)?.error || axErr?.message || "Unknown error";
    console.error("Failed to refresh access token:", errMsg);
    return null; // Return null, not error message
  }
}

// Accepts a partial settings patch: the server merges per field ($set), so callers
// should send ONLY the fields they intend to change and never a stale full snapshot
// (review R16).
export async function setCommonSettings(settings: Partial<T.Settings>): Promise<T.HTTPRequestSuccessFlag> {
  try {

    const config = {
      headers: await getAuthHeader(),
    }

    const res = await instance.put("/api/users/user-common-settings", settings, config);

    return res.data.success;

  } catch (err: unknown) {
    const axErr = err instanceof AxiosError ? err : null;
    const data = axErr?.response?.data as AxiosErrorData | undefined;
    throw new ErrorResponse(data?.message || "Failed to update settings", axErr?.response?.status || 500);
  }
}

export async function pdfUpload(formData: FormData): Promise<T.HTTPRequestSuccessFlag> {
  try {
    const config = {
      headers: await getAuthHeader(),
      'Content-Type': 'multipart/form-data'
    }
    const res = await instance.post('/api/users/upload-pdf', formData, config)

    return res.data.success;

  } catch (err: unknown) {
    const axErr = err instanceof AxiosError ? err : null;
    const data = axErr?.response?.data as AxiosErrorData | undefined;
    throw new ErrorResponse(data?.message || "Failed to upload PDF", axErr?.response?.status || 500);
  }
};

export type ChangePasswordResponse = {
  success: boolean;
  message?: string;
}

export type EmailChangeResponse = {
  success: boolean;
  message?: string;
  pendingEmail?: string;
}

export async function requestEmailChange(newEmail: string): Promise<EmailChangeResponse> {
  try {
    const config = {
      headers: await getAuthHeader(),
    }

    const res = await instance.post(
      "/api/auth/request-email-change",
      { newEmail },
      config
    );

    return {
      success: res.data.success,
      message: res.data.message,
      pendingEmail: res.data.pendingEmail
    };

  } catch (err: unknown) {
    const data = (err instanceof AxiosError ? err.response?.data : null) as AxiosErrorData | null;
    return {
      success: false,
      message: data?.message || "Failed to request email change"
    };
  }
}

// ── Impersonation (non-production only) ──────────────────────────────

export type ImpersonationTarget = {
  _id: string;
  username: string;
  email: string;
  userRole: T.UserRole;
  userRegion: T.UserRegion;
}

export type ImpersonateResponse = {
  success: boolean;
  token: string;
  targetUser: ImpersonationTarget;
}

export type EndImpersonationResponse = {
  success: boolean;
  token: string;
  admin: ImpersonationTarget;
}

export type ImpersonationSearchResult = {
  _id: string;
  username: string;
  email: string;
  userRole: T.UserRole;
  userRegion: T.UserRegion;
  firmName?: string;
}

export async function searchUsersForImpersonation(
  query: string,
  filters?: { region?: string; role?: string }
): Promise<ImpersonationSearchResult[]> {
  const params: Record<string, string> = {};
  if (query) params.q = query;
  if (filters?.region) params.region = filters.region;
  if (filters?.role) params.role = filters.role;
  const res = await apiGet<{ success: boolean; users: ImpersonationSearchResult[] }>(
    "/api/auth/impersonate/search",
    params
  );
  return res.users;
}

export async function impersonateUser(targetUserId: string): Promise<ImpersonateResponse> {
  const config = { headers: await getAuthHeader() };
  const res = await instance.post("/api/auth/impersonate", { targetUserId }, config);
  return res.data;
}

export async function endImpersonationApi(): Promise<EndImpersonationResponse> {
  const config = { headers: await getAuthHeader() };
  const res = await instance.post("/api/auth/end-impersonation", {}, config);
  return res.data;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
  try {
    const config = {
      headers: await getAuthHeader(),
    }

    const res = await instance.put(
      "/api/auth/change-password",
      { currentPassword, newPassword },
      config
    );

    return {
      success: res.data.success,
      message: res.data.message
    };

  } catch (err: unknown) {
    const data = (err instanceof AxiosError ? err.response?.data : null) as AxiosErrorData | null;
    return {
      success: false,
      message: data?.message || "Failed to change password"
    };
  }
}

